import * as React from "react"
import { cn } from "~/common/cn"

interface ResponsiveTableProps {
  children: React.ReactNode
  className?: string
}

interface ResponsiveTableHeaderProps {
  children?: React.ReactNode
  className?: string
}

interface ResponsiveTableBodyProps {
  children?: React.ReactNode
  className?: string
}

interface ResponsiveTableRowProps {
  className?: string
  desktopContent: React.ReactNode
  mobileContent: React.ReactNode
  onClick?: () => void
}

interface ResponsiveTableCellProps {
  children?: React.ReactNode
  className?: string
  span?: number
  onClick?: () => void
}

const ResponsiveTable = React.forwardRef<HTMLDivElement, ResponsiveTableProps>(
  ({ className, children, ...props }, ref) => (
    <div ref={ref} className={cn("space-y-0", className)} {...props}>
      {children}
    </div>
  )
)
ResponsiveTable.displayName = "ResponsiveTable"

const ResponsiveTableHeader = React.forwardRef<HTMLDivElement, ResponsiveTableHeaderProps>(
  ({ className, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "hidden grid-cols-12 border-b text-sm font-medium text-muted-foreground lg:grid",
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
)
ResponsiveTableHeader.displayName = "ResponsiveTableHeader"

const ResponsiveTableBody = React.forwardRef<HTMLDivElement, ResponsiveTableBodyProps>(
  ({ className, children, ...props }, ref) => (
    <div ref={ref} className={className} {...props}>
      {children}
    </div>
  )
)
ResponsiveTableBody.displayName = "ResponsiveTableBody"

const ResponsiveTableRow = React.forwardRef<HTMLDivElement, ResponsiveTableRowProps>(
  ({ className, desktopContent, mobileContent, onClick, ...props }, ref) => (
    <div ref={ref} className={className} {...props}>
      {/* Desktop Table View */}
      <div
        className={cn(
          "group hidden grid-cols-12 border-b hover:bg-postal-purple-hover lg:grid",
          onClick && "cursor-pointer"
        )}
        onClick={onClick}
      >
        {desktopContent}
      </div>

      {/* Mobile Card View */}
      <div
        className={cn(
          "mb-4 rounded-lg border bg-white p-4 shadow-sm lg:hidden",
          onClick && "cursor-pointer hover:bg-postal-purple-hover"
        )}
        onClick={onClick}
      >
        {mobileContent}
      </div>
    </div>
  )
)
ResponsiveTableRow.displayName = "ResponsiveTableRow"

const ResponsiveTableCell = React.forwardRef<HTMLDivElement, ResponsiveTableCellProps>(
  ({ className, children, span = 1, ...props }, ref) => {
    const getColSpanClass = (span: number) => {
      switch (span) {
        case 1:
          return "col-span-1"
        case 2:
          return "col-span-2"
        case 3:
          return "col-span-3"
        case 4:
          return "col-span-4"
        case 5:
          return "col-span-5"
        case 6:
          return "col-span-6"
        case 7:
          return "col-span-7"
        case 8:
          return "col-span-8"
        case 9:
          return "col-span-9"
        case 10:
          return "col-span-10"
        case 11:
          return "col-span-11"
        case 12:
          return "col-span-12"
        default:
          return "col-span-1"
      }
    }

    return (
      <div
        ref={ref}
        className={cn(
          `${getColSpanClass(span)} flex items-center border-r p-4 last:border-r-0`,
          className
        )}
        {...props}
      >
        {children}
      </div>
    )
  }
)
ResponsiveTableCell.displayName = "ResponsiveTableCell"

interface ResponsiveTableHeaderCellProps {
  children?: React.ReactNode
  className?: string
  span?: number
}

const ResponsiveTableHeaderCell = React.forwardRef<HTMLDivElement, ResponsiveTableHeaderCellProps>(
  ({ className, children, span = 1, ...props }, ref) => {
    const getColSpanClass = (span: number) => {
      switch (span) {
        case 1:
          return "col-span-1"
        case 2:
          return "col-span-2"
        case 3:
          return "col-span-3"
        case 4:
          return "col-span-4"
        case 5:
          return "col-span-5"
        case 6:
          return "col-span-6"
        case 7:
          return "col-span-7"
        case 8:
          return "col-span-8"
        case 9:
          return "col-span-9"
        case 10:
          return "col-span-10"
        case 11:
          return "col-span-11"
        case 12:
          return "col-span-12"
        default:
          return "col-span-1"
      }
    }

    return (
      <div
        ref={ref}
        className={cn(`${getColSpanClass(span)} border-r p-4 last:border-r-0`, className)}
        {...props}
      >
        {children}
      </div>
    )
  }
)
ResponsiveTableHeaderCell.displayName = "ResponsiveTableHeaderCell"

interface MobileCardHeaderProps {
  children: React.ReactNode
  className?: string
}

const MobileCardHeader = React.forwardRef<HTMLDivElement, MobileCardHeaderProps>(
  ({ className, children, ...props }, ref) => (
    <div ref={ref} className={cn("mb-3 flex items-start justify-between", className)} {...props}>
      {children}
    </div>
  )
)
MobileCardHeader.displayName = "MobileCardHeader"

interface MobileCardContentProps {
  children: React.ReactNode
  className?: string
}

const MobileCardContent = React.forwardRef<HTMLDivElement, MobileCardContentProps>(
  ({ className, children, ...props }, ref) => (
    <div ref={ref} className={cn("space-y-2", className)} {...props}>
      {children}
    </div>
  )
)
MobileCardContent.displayName = "MobileCardContent"

interface MobileCardRowProps {
  label: string
  children: React.ReactNode
  className?: string
}

const MobileCardRow = React.forwardRef<HTMLDivElement, MobileCardRowProps>(
  ({ className, label, children, ...props }, ref) => (
    <div ref={ref} className={cn("flex items-center justify-between", className)} {...props}>
      <span className="text-sm text-muted-foreground">{label}:</span>
      <div>{children}</div>
    </div>
  )
)
MobileCardRow.displayName = "MobileCardRow"

export {
  MobileCardContent,
  MobileCardHeader,
  MobileCardRow,
  ResponsiveTable,
  ResponsiveTableBody,
  ResponsiveTableCell,
  ResponsiveTableHeader,
  ResponsiveTableHeaderCell,
  ResponsiveTableRow,
}
